import type { AdminTrialPolicy } from "./types";
import { request } from "./api-client";

export type { AdminTrialPolicy };

/**
 * What the route actually sends.
 *
 * Every admin route on this router answers `{ data: … }` and `request<T>` does **not** unwrap it —
 * it is a bare `(await response.json()) as T` (`shared/src/utils/api-client.ts:315`), so the type
 * argument is an unchecked assertion and a wrong one compiles. The sibling services live with that
 * by declaring the envelope in their own return type (`listPlans(): Promise<{ data: … }>`) and
 * making every caller reach through it.
 *
 * This file unwraps once instead, so the card is written against the policy rather than against a
 * transport detail. The two lines below are the only place in `admin` that knows both shapes.
 *
 * **This was a live defect, found by the stage-9 walk and by nothing else.** `getTrialPolicy` used
 * to claim `Promise<AdminTrialPolicy>` while returning the envelope. The card then read
 * `policy.data.lastChange` as `undefined`, `provenanceSentence` dereferenced `.byName` on it, and
 * `/billing/plans` rendered *"Something went wrong — Cannot read properties of undefined (reading
 * 'byName')"* for every ADMIN on deployed dev. The unit tests could not see it: they mock
 * `request`, and the mock returned the unwrapped shape, so the assertion was against the double.
 * The tests now mock the envelope, which is what the server sends.
 */
interface Enveloped {
  data: AdminTrialPolicy;
}

/**
 * Kept as a named function rather than inlined `.data` twice, so the two call sites cannot drift
 * and the reason above has one thing to point at.
 */
function unwrap(response: Enveloped): AdminTrialPolicy {
  return response.data;
}

/**
 * Read the configured Pro+ trial length and who last changed it.
 *
 * ADMIN-only on the server. **Do not call this below ADMIN.** A SUPPORT operator sees the
 * card without the value, and the reason is not only that the number is none of their
 * business: the refusal is not readable in a browser. `cors()` sets its headers after
 * `await next()` and `requireRole` throws before that, so a 403 reaches the page as
 * `Failed to fetch` — indistinguishable from the API being down. The card therefore gates
 * on the role it already holds rather than discovering the refusal.
 */
export async function getTrialPolicy(): Promise<AdminTrialPolicy> {
  return unwrap(await request<Enveloped>("/subscription-management/trial-policy"));
}

/**
 * Set the trial length for NEW grants, with a reason.
 *
 * Returns the policy as written, re-read by the server — so the caller does not assemble a
 * result from its own request body plus a guess at the timestamp. The server's `updatedAt`
 * is the truth and a client-side `new Date()` would disagree with the stored row by the
 * round trip.
 *
 * `reason` is trimmed here as well as on the server. The server's schema is
 * `.trim().min(1)`, so a reason of only spaces is refused — and a client that sent it
 * unsent-trimmed would meet a bare validation failure instead of the message written for
 * exactly that case.
 */
export async function updateTrialPolicy(input: {
  durationDays: number;
  reason: string;
}): Promise<AdminTrialPolicy> {
  return unwrap(
    await request<Enveloped>("/subscription-management/trial-policy", {
      method: "PUT",
      body: JSON.stringify({ durationDays: input.durationDays, reason: input.reason.trim() }),
    }),
  );
}
